import Channel, { ThreadChannel } from '@@types/Channel';
import { CHANNEL_ID } from '@@types/common';

import { ChannelFlag, ChannelType, MAIN_THREAD_CHANNEL_ID } from '~/constants';
import i18n from '~/i18n';
import { numberToColor } from '~/utils/colorUtil';
import { isNonEmpty, isNonEquals, isNonNil } from '~/utils/fnUtil/fnUtil';
import { isEmpty } from 'lodash';
import {
  __,
  allPass,
  complement,
  converge,
  descend,
  gt,
  pipe,
  prop,
  propEq,
  propOr,
  propSatisfies,
  sortWith,
  startsWith,
} from 'ramda';
import curry from 'ramda/es/curry';
import filter from 'ramda/es/filter';
import flatten from 'ramda/es/flatten';
import pluck from 'ramda/es/pluck';
import uniq from 'ramda/es/uniq';
import { Either, Maybe } from 'ramda-fantasy';

type IsDisplayedChannel = (value: { displayed?: boolean }) => boolean;

export const isBotChannel = allPass([isNonNil, propEq('type', ChannelType.Bot)]);

export const isNonBotChannel = complement(isBotChannel);

export const isDirectChannel = allPass([isNonNil, propEq('type', ChannelType.Direct)]);

export const isNonDirectChannel = complement(isDirectChannel);

export const isPrivateChannel = allPass([isNonNil, propEq('type', ChannelType.Private)]);

export const isNonPrivateChannel = complement(isPrivateChannel);

export const isMeChannel = allPass([isNonNil, propEq('type', ChannelType.Me)]);

export const isMainThreadChannel = allPass([isNonNil, propEq('channelId', MAIN_THREAD_CHANNEL_ID)]);

export const isMainThreadChannelId = (channelId: CHANNEL_ID) => channelId === MAIN_THREAD_CHANNEL_ID;

export const isArchivedChannel = allPass([isNonNil, propEq('flag', ChannelFlag.Archived)]);

export const isNonArchivedChannel = complement(isArchivedChannel);

export const isThreadChannel = (channel: { type: ChannelType }): channel is ThreadChannel =>
  allPass([isNonNil, propEq('type', ChannelType.Thread)])(channel);

export const canUploadChannel = allPass([isNonNil, isNonBotChannel, isNonArchivedChannel]);

export const isSharedChannel = (channel) => channel?.shared ?? false;

export const makeDummyChannel = (id: string, userId: string) => ({
  channelId: `dummy-${id}`,
  opponentId: id,
  members: [id, userId],
  type: ChannelType.Direct,
  unreadCount: 0,
  displayed: true,
  updatedAt: -1, // 정렬을 위해 지정
});

export const sortByUpdatedAt = sortWith<{ updatedAt: number | string }>([descend(prop('updatedAt'))]);

// calcFirstNewLogSeq :: Channel a => a -> Maybe Number
export const calcFirstNewLogSeq = (channel) => {
  if (isEmpty(channel)) return Maybe.Nothing();
  return channel.readSeq >= channel.seq ? Maybe.Nothing() : Maybe.Just(channel.readSeq);
};

export const getNoMemberTitle = () => {
  return i18n.t('noMemberChannelTitle', '멤버 없음');
};

export const isEmptyTitle = pipe(propOr('', 'title'), isEmpty);

export const isDummyChannelId = startsWith('dummy');

export const isTopicChannel = allPass([propSatisfies(isNonEmpty, 'title'), isPrivateChannel]);

export const isNonTopicChannel: (channel: Channel) => boolean = complement(isTopicChannel);

export const validateTitle = (title) => {
  return isEmpty(title) || title.length > 30 ? Either.Left(title) : Either.Right(title);
};

export const validateDescription = (description) => {
  return description.length > 200 ? Either.Left(description) : Either.Right(description);
};

export const isDisplayedChannel: IsDisplayedChannel = prop('displayed');

export const isHiddenChannel: IsDisplayedChannel = complement(isDisplayedChannel);

export const isUnreadChannel = propSatisfies(gt(__, 0), 'unreadCount');

export const isReadSeqNotMatchSeq = converge<{ readSeq?: number; seq?: number }[], boolean, number, number>(
  isNonEquals,
  [prop('readSeq'), prop('seq')]
);

export const isUnreadHiddenChannel = allPass([isHiddenChannel, isUnreadChannel]);

export const getDefaultChannelColor = (channel) => {
  return numberToColor(channel.channelId);
};

export const getChannelsMemberIdsBy = curry((pred: (channel: Channel) => boolean, channels: Channel[]) => {
  return pipe<any, any, any, any, any>(filter(pred), pluck('members'), flatten, uniq)(channels);
});

export const getNonTopicChannelsMemberIds = getChannelsMemberIdsBy(isNonTopicChannel);

export const getTopicChannelsMemberIds = getChannelsMemberIdsBy(isTopicChannel);
